// RouteLink.js
import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";

// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("./Pages/Login/LoginPage"));
const DashboardLayout = React.lazy(() => import("./Layout/DashboardLayout"));
const DashboardHome = React.lazy(() =>
  import("./Pages/Dashboard/DashboardHome")
);
const PrivateRoute = React.lazy(() => import("./Utilities/PrivateRoute"));
const Category = React.lazy(() => import("./Pages/Category/Category"));
const AddCategory = React.lazy(() => import("./Pages/Category/AddCategory"));
const SubCategory = React.lazy(() => import("./Pages/SubCategory/SubCategory"));
const AddSubCategory = React.lazy(() => import("./Pages/SubCategory/AddSubCategory"));
const UOM = React.lazy(() => import("./Pages/UOM/UOM"));
const AddUOM = React.lazy(() => import("./Pages/UOM/AddUOM"));
const Location = React.lazy(() => import("./Pages/Location/Location"));
const AddLocation = React.lazy(() => import("./Pages/Location/AddLocation"));
const Vendor = React.lazy(() => import("./Pages/Vendor/Vendor"));
const AddVendor = React.lazy(() => import("./Pages/Vendor/AddVendor"));
const ItemType = React.lazy(() => import("./Pages/ItemType/ItemType"));
const AddItemType = React.lazy(() => import("./Pages/ItemType/AddItemType"));
const Item = React.lazy(() => import("./Pages/Items/Item"));
const AddItem = React.lazy(() => import("./Pages/Items/AddItem"));
const ManufacturingStage = React.lazy(() => import("./Pages/ManufacturingStage/ManufacturingStage"));
const AddManufacturingStage = React.lazy(() => import("./Pages/ManufacturingStage/AddManufacturingStage"));
const SemiFinishedItemStage = React.lazy(() => import("./Pages/SemiFinishedItemStage/SemiFinishedItemStage"));
const AddSemiFinishedItemStage = React.lazy(() => import("./Pages/SemiFinishedItemStage/AddSemiFinishedItemStage"));
const WorkCenter = React.lazy(() => import("./Pages/WorkCenter/WorkCenter"));
const AddWorkCenter = React.lazy(() => import("./Pages/WorkCenter/AddWorkCenter"));
const Operations = React.lazy(() => import("./Pages/Operations/Operations"));
const AddOperations = React.lazy(() => import("./Pages/Operations/AddOperations"));
const BatterySpecification = React.lazy(() => import("./Pages/BatterySpecification/BatterySpecification"));
const AddBatterySpecification = React.lazy(() => import("./Pages/BatterySpecification/AddBatterySpecification"));
const RoutingHead = React.lazy(() => import("./Pages/RoutingHead/RoutingHead"));
const AddRoutingHead = React.lazy(() => import("./Pages/RoutingHead/AddRoutingHead"));
const BOM = React.lazy(() => import("./Pages/BOM/BOM"));
const AddBOM = React.lazy(() => import("./Pages/BOM/AddBOM"));
const CurrentStock = React.lazy(() => import("./Pages/CurrentStock/CurrentStock"));
const ProductionOrder = React.lazy(() => import("./Pages/Production/ProductionOrder"));
const ProductionOrderList = React.lazy(() => import("./Pages/Production/ProductionOrderList"));
const Purchase = React.lazy(() => import("./Pages/Purchase/Purchase"));
const PurchaseList = React.lazy(() => import("./Pages/Purchase/PurchaseList"));

const routes = [
  {
    path: "/login",
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    element: (
      <Suspense fallback={<div>Loading...</div>}>
        <PrivateRoute />
      </Suspense>
    ), // 🛡 Wrap protected routes here
    children: [
      {
        path: "/dashboard",
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <DashboardLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <CurrentStock />
              </Suspense>
            ),
          },
          {
            path: "category",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Category />
              </Suspense>
            ),
          },
          {
            path: "categoryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddCategory />
              </Suspense>
            ),
          },
          {
            path: "subcategory",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SubCategory />
              </Suspense>
            ),
          },
          {
            path: "subcategoryadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddSubCategory />
              </Suspense>
            ),
          },
          {
            path: "uom",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <UOM />
              </Suspense>
            ),
          },
          {
            path: "uomadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddUOM />
              </Suspense>
            ),
          },
          {
            path: "location",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Location />
              </Suspense>
            ),
          },
          {
            path: "locationadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddLocation />
              </Suspense>
            ),
          },
          {
            path: "vendor",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Vendor />
              </Suspense>
            ),
          },
          {
            path: "vendoradd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddVendor />
              </Suspense>
            ),
          },
          {
            path: "itemtype",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ItemType />
              </Suspense>
            ),
          },
          {
            path: "itemtypeadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddItemType />
              </Suspense>
            ),
          },
          {
            path: "items",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Item />
              </Suspense>
            ),
          },
          {
            path: "itemadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddItem />
              </Suspense>
            ),
          },
          {
            path: "manufacturingstage",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ManufacturingStage />
              </Suspense>
            ),
          },
          {
            path: "manufacturingstageadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddManufacturingStage />
              </Suspense>
            ),
          },
          {
            path: "semifinisheditemstage",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <SemiFinishedItemStage />
              </Suspense>
            ),
          },
          {
            path: "semifinisheditemstageadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddSemiFinishedItemStage />
              </Suspense>
            ),
          },
          {
            path: "workcenter",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <WorkCenter />
              </Suspense>
            ),
          },
          {
            path: "workcenteradd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddWorkCenter />
              </Suspense>
            ),
          },
          {
            path: "operations",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Operations />
              </Suspense>
            ),
          },
          {
            path: "operationsadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddOperations />
              </Suspense>
            ),
          },
          {
            path: "batteryspecification",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <BatterySpecification />
              </Suspense>
            ),
          },
          {
            path: "batteryspecificationadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddBatterySpecification />
              </Suspense>
            ),
          },
          {
            path: "routinghead",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <RoutingHead />
              </Suspense>
            ),
          },
          {
            path: "routingheadadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddRoutingHead />
              </Suspense>
            ),
          },
          {
            path: "bom",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <BOM />
              </Suspense>
            ),
          },
          {
            path: "bomadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <AddBOM />
              </Suspense>
            ),
          },
          {
            path: "currentstock",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <CurrentStock />
              </Suspense>
            ),
          },
          {
            path: "productionorder",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProductionOrderList />
              </Suspense>
            ),
          },
          {
            path: "productionorder/new",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <ProductionOrder />
              </Suspense>
            ),
          },
          {
            path: "purchase",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <PurchaseList />
              </Suspense>
            ),
          },
          {
            path: "purchaseadd",
            element: (
              <Suspense fallback={<div>Loading...</div>}>
                <Purchase />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
];

export default routes;

// // RouteLink.jsOld Routing File
// import React from "react";
// import { Navigate } from "react-router-dom";
// import LoginPage from "./Pages/Login/LoginPage";
// import DashboardLayout from "./Layout/DashboardLayout";
// import DashboardHome from "./Pages/Dashboard/DashboardHome";
// import PrivateRoute from "./Utilities/PrivateRoute";
// import Village from "./Pages/Village/Village";
// import AddVillage from "./Pages/Village/AddVillage";
// import Booth from "./Pages/Booth/Booth";
// import AddBooth from "./Pages/Booth/AddBooth";
// import Citizen from "./Pages/Citizen/Citizen";
// import AddCitizen from "./Pages/Citizen/AddCitizen";
// import Grampanchayat from "./Pages/Grampanchayat/Grampanchayat";
// import AddGrampanchayat from "./Pages/Grampanchayat/AddGrampanchayat";
// import BoothCoordinator from "./Pages/BoothCoordinator/BoothCoordinator";
// import AddBoothCoordinator from "./Pages/BoothCoordinator/AddBoothCoordinator";
// import BoothAssign from "./Pages/BoothAssign/BoothAssign";
// import AddBoothAssign from "./Pages/BoothAssign/AddBoothAssign";
// import Cluster from "./Pages/Cluster/Cluster";
// import AddCluster from "./Pages/Cluster/AddCluster";
// import Zone from "./Pages/Zone/Zone";
// import AddZone from "./Pages/Zone/AddZone";
// import BoothVillageMapping from "./Pages/BoothVillageMapping/BoothVillageMapping";
// import AddBoothVillageMapping from "./Pages/BoothVillageMapping/AddBoothVillageMapping";

// // const routes = [
// //   {
// //     path: '/login',
// //     element: <LoginPage />,
// //   },
// //   {
// //     path: '/',
// //     element: <Navigate to="/login" replace />,
// //   },
// //   {
// //     path: '/dashboard',
// //     element: <DashboardLayout />,
// //     children: [
// //       { index: true, element: <DashboardHome /> },
// //       { path: 'add-user', element: <AddUser /> },
// //       { path: 'delete-user', element: <DeleteUser /> },
// //       { path: 'state', element: <StatePage /> },
// //       { path: 'city', element: <CityPage /> },
// //       { path: 'add-property', element: <AddProperty /> },
// //       { path: 'delete-property', element: <DeleteProperty /> },
// //     ],
// //   },
// // ];

// const routes = [
//   {
//     path: "/login",
//     element: <LoginPage />,
//   },
//   {
//     path: "/",
//     element: <Navigate to="/login" replace />,
//   },
//   {
//     element: <PrivateRoute />, // 🛡 Wrap protected routes here
//     children: [
//       {
//         path: "/dashboard",
//         element: <DashboardLayout />,
//         children: [
//           { index: true, element: <DashboardHome /> },
//           //
//           { path: "zone", element: <Zone /> },
//           { path: "zoneadd", element: <AddZone /> },
//           //
//           { path: "cluster", element: <Cluster /> },
//           { path: "clusteradd", element: <AddCluster /> },

//           { path: "grampanchayat", element: <Grampanchayat /> },
//           { path: "grampanchayatadd", element: <AddGrampanchayat /> },
//           //
//           { path: "village", element: <Village /> },
//           { path: "villageadd", element: <AddVillage /> },
//           //
//           { path: "booth", element: <Booth /> },
//           { path: "boothadd", element: <AddBooth /> },
//           // Booth Coordinator
//           { path: "boothcoordinator", element: <BoothCoordinator /> },
//           { path: "boothcoordinatoradd", element: <AddBoothCoordinator /> },
//           //
//           { path: "boothcoordinatorassign", element: <BoothAssign /> },
//           { path: "boothcoordinatorassignadd", element: <AddBoothAssign /> },

//           { path: "boothvillagemapping", element: <BoothVillageMapping /> },
//           {
//             path: "boothvillagemappingadd",
//             element: <AddBoothVillageMapping />,
//           },
//           //
//           //
//           { path: "citizen", element: <Citizen /> },
//           { path: "citizenadd", element: <AddCitizen /> },
//         ],
//       },
//     ],
//   },
// ];

// export default routes;
